// Which portal the SPA is running inside. The portal loaders (deploy/<portal>/index.html)
// full-screen-iframe the app with a ?portal=<name> marker; a top-level (non-iframed)
// load is a "direct" login. Captured ONCE at module load, before any SPA routing can
// strip the query string.
export type Portal =
  | 'itch' | 'crazygames' | 'newgrounds' | 'gamejolt' | 'indiedb' | 'y8' | 'direct';

function detectPortal(): Portal {
  try {
    const p = new URLSearchParams(window.location.search).get('portal');
    if (p === 'itch' || p === 'crazygames' || p === 'newgrounds' || p === 'gamejolt'
        || p === 'indiedb' || p === 'y8' || p === 'direct')
      return p;
    // Y8 embeds the app from THEIR page rather than from a loader of ours, so the
    // `?portal=y8` marker only survives if they kept the query string we gave them.
    // Their SDK is mandatory for the review, and it only loads when this returns 'y8',
    // so recognise the frame itself as a second source of truth. `ancestorOrigins` is
    // Chrome/Safari; Firefox has no such list, hence the referrer fallback (the parent
    // document's URL, which is readable cross-origin).
    if (framedBy('y8.com')) return 'y8';

    // No explicit marker: a cross-site iframe means a portal (assume itch, the
    // original portal whose loader predates the marker); top-level = direct.
    //
    // Note this cannot recognise a LINKED portal such as Indie DB, where the player arrives by
    // a top-level navigation rather than inside a frame — there is nothing to detect, so the
    // `?portal=` marker on the link is the only thing carrying the attribution. Keep it on any
    // URL published there.
    return window.self !== window.top ? 'itch' : 'direct';
  } catch {
    // Accessing window.top across origins can throw — that only happens when framed.
    return 'itch';
  }
}

// Is this document framed by `domain` (or one of its subdomains)? False at top level,
// and false whenever neither signal is available — never guess a portal into existence.
function framedBy(domain: string): boolean {
  try {
    if (window.self === window.top) return false;
    const origins = window.location.ancestorOrigins;
    const candidates = origins && origins.length
      ? Array.from(origins)
      : (document.referrer ? [document.referrer] : []);
    return candidates.some(u => {
      try {
        const h = new URL(u).hostname;
        return h === domain || h.endsWith(`.${domain}`);
      } catch { return false; }
    });
  } catch {
    return false;
  }
}

export const PORTAL: Portal = detectPortal();
export const isCrazyGames = PORTAL === 'crazygames';
export const isY8 = PORTAL === 'y8';
